import { useState, useMemo } from "react";

import { useBitrixDeal } from "./hooks/useBitrixDeal";
import { useDealRegion } from "./hooks/useDealRegion";
import { useCatalogData } from "./hooks/useCatalogData";
import { useCatalogSearch } from "./hooks/useCatalogSearch";
import { useMeasureOptions } from "./hooks/useMeasureOptions";
import { useCart } from "./hooks/useCart";

import { groupCatalogItems, groupItemsByField, groupItemsByTree } from "./utils/catalogGrouping";

import CatalogHeader from "./components/CatalogHeader";
import CatalogList from "./components/CatalogList";
import CartPanel from "./components/CartPanel";
import CatalogTabs from "./components/CatalogTabs";
import CatalogGroupedList from "./components/CatalogGroupedList";

import styles from "./ProductCatalog.styles";

const ProductCatalog = () => {
    const { isBitrix, dealId } = useBitrixDeal();
    const { region, manualRegion, setManualRegion } = useDealRegion(dealId);
    const { catalogData, loading } = useCatalogData();
    const { getMeasureCode } = useMeasureOptions();

    const [priceBasis, setPriceBasis] = useState("cash"); // 'cash' | 'noncash'
    const [catalogView, setCatalogView] = useState("catalog");
    const [search, setSearch] = useState("");
    const [expandedName, setExpandedName] = useState(null);
    const { smartScoreByName, isSmartSearching, debouncedSearch } = useCatalogSearch(search);

    const {
        cart,
        rowQuantities,
        submitting,
        handleQuantityChange,
        handleAddRowToCart,
        updateCartQuantity,
        removeFromCart,
        cartItemPrice,
        cartTotal,
        cartCount,
        handleAddToDeal,
    } = useCart({ priceBasis, dealId, getMeasureCode });

    // Если авто-регион (по адресу сделки) не определился, даём выбрать вручную
    const effectiveRegion = region || manualRegion;

    const availableRegions = useMemo(() => {
        return [...new Set(catalogData.map((item) => item.region))].filter(Boolean).sort();
    }, [catalogData]);

    // Список фильтруется по debouncedSearch (не по живому search) — тот же
    // такт, что и у smartScoreByName, чтобы карта не могла отфильтровать список
    // по более старому тексту, чем сейчас показан в поле ввода.
    const groupedItems = useMemo(
        () => groupCatalogItems(catalogData, { region: effectiveRegion, search: debouncedSearch, priceBasis, smartScoreByName }),
        [catalogData, effectiveRegion, debouncedSearch, priceBasis, smartScoreByName]
    );

    // Вкладка "По категориям" — дерево разделов Битрикса: корневой раздел ->
    // раздел -> номенклатуры. Поля root_section/section приходят с бэкенда из
    // category_section_mapping, сопоставление идёт по имени категории.
    const groupedByCategory = useMemo(
        () => groupItemsByTree(
            groupedItems,
            ["root_section", "section"],
            priceBasis
        ),
        [groupedItems, priceBasis]
    );

    const groupedByProduction = useMemo(
        () => groupItemsByField(
            groupedItems,
            "manufacturer",
            priceBasis
        ),
        [groupedItems, priceBasis]
    );

    return (
        <div className="pc">
            <style>{styles}</style>

            <CatalogHeader
                itemCount={catalogData.length}
                isBitrix={isBitrix}
                search={search}
                onSearchChange={setSearch}
                isSmartSearching={isSmartSearching}
                priceBasis={priceBasis}
                onPriceBasisChange={setPriceBasis}
                region={region}
                manualRegion={manualRegion}
                onManualRegionChange={setManualRegion}
                availableRegions={availableRegions}
            />

            <CatalogTabs
                value={catalogView}
                onChange={setCatalogView}
            />

            <div className="pc-body">
                {catalogView === "all" && (
                    <CatalogList
                        loading={loading}
                        groupedItems={groupedItems}
                        expandedName={expandedName}
                        onToggleExpand={(name) =>
                            setExpandedName(
                                expandedName === name
                                    ? null
                                    : name
                            )
                        }
                        priceBasis={priceBasis}
                        rowQuantities={rowQuantities}
                        onQuantityChange={handleQuantityChange}
                        onAddRow={handleAddRowToCart}
                    />
                )}

                {catalogView === "catalog" && (
                    <CatalogGroupedList
                        loading={loading}
                        groups={groupedByCategory}
                        expandedName={expandedName}
                        onToggleExpand={(name) =>
                            setExpandedName(
                                expandedName === name
                                    ? null
                                    : name
                            )
                        }
                        priceBasis={priceBasis}
                        rowQuantities={rowQuantities}
                        onQuantityChange={handleQuantityChange}
                        onAddRow={handleAddRowToCart}
                    />
                )}

                {catalogView === "production" && (
                    <CatalogGroupedList
                        loading={loading}
                        groups={groupedByProduction}
                        expandedName={expandedName}
                        onToggleExpand={(name) =>
                            setExpandedName(
                                expandedName === name
                                    ? null
                                    : name
                            )
                        }
                        priceBasis={priceBasis}
                        rowQuantities={rowQuantities}
                        onQuantityChange={handleQuantityChange}
                        onAddRow={handleAddRowToCart}
                    />
                )}

                <CartPanel
                    cart={cart}
                    cartCount={cartCount}
                    cartTotal={cartTotal}
                    cartItemPrice={cartItemPrice}
                    onUpdateQuantity={updateCartQuantity}
                    onRemove={removeFromCart}
                    onSubmit={handleAddToDeal}
                    submitting={submitting}
                    dealId={dealId}
                />
            </div>
        </div>
    );
};

export default ProductCatalog;
